import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import {
  FIELD_OPERATOR_LABELS,
  OPERATOR_LABELS,
  periodShortcut,
  type FieldFilterValue,
  type IdFilterValue,
  type IdOperator,
  type LeadFilterState,
} from '../../lib/lead-filter-model';
import {
  customFields,
  listAttendants,
  listJobTitles,
  listLeads,
  listPipelines,
  listStages,
  lists as listsApi,
  products as productsApi,
  tags as tagsApi,
} from '../../lib/resources';
import { Button, inputClass } from '../ui';

interface Option {
  id: string;
  label: string;
  color?: string | null;
}

/** "operação + lista com busca" — o mesmo controle para tags, listas, produtos, atendente… */
function IdFilterControl({
  value,
  options,
  onChange,
  operators = ['some', 'every', 'none'],
  emptyLabel,
}: {
  value: IdFilterValue | undefined;
  options: Option[];
  onChange: (value: IdFilterValue | undefined) => void;
  operators?: IdOperator[];
  emptyLabel?: string;
}) {
  const [term, setTerm] = useState('');
  const current = value ?? { operator: operators[0], ids: [] };
  const visible = options.filter((option) =>
    option.label.toLowerCase().includes(term.trim().toLowerCase()),
  );
  const toggle = (id: string) => {
    const ids = current.ids.includes(id)
      ? current.ids.filter((item) => item !== id)
      : [...current.ids, id];
    onChange(ids.length ? { operator: current.operator, ids } : undefined);
  };

  return (
    <div className="space-y-2">
      <select
        className={`${inputClass} mt-0`}
        value={current.operator}
        disabled={current.empty}
        onChange={(event) =>
          onChange(
            current.ids.length
              ? { operator: event.target.value as IdOperator, ids: current.ids }
              : undefined,
          )
        }
      >
        {operators.map((operator) => (
          <option key={operator} value={operator}>
            {OPERATOR_LABELS[operator]}
          </option>
        ))}
      </select>
      <input
        className={`${inputClass} mt-0`}
        placeholder="Buscar..."
        value={term}
        onChange={(event) => setTerm(event.target.value)}
      />
      <div className="max-h-44 space-y-1 overflow-y-auto">
        {emptyLabel && (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={Boolean(current.empty)}
              onChange={(event) =>
                onChange(
                  event.target.checked ? { operator: 'some', ids: [], empty: true } : undefined,
                )
              }
            />
            <em>{emptyLabel}</em>
          </label>
        )}
        {visible.map((option) => (
          <label key={option.id} className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              disabled={current.empty}
              checked={current.ids.includes(option.id)}
              onChange={() => toggle(option.id)}
            />
            {option.color && (
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: option.color }}
              />
            )}
            {option.label}
          </label>
        ))}
        {visible.length === 0 && <p className="text-xs text-slate-400">Nada encontrado.</p>}
      </div>
    </div>
  );
}

function Section({
  title,
  active,
  children,
}: {
  title: string;
  active: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(active);
  return (
    <div className="border-b border-slate-100">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-slate-700"
      >
        <span>
          {title}
          {active && <span className="ml-2 inline-block h-2 w-2 rounded-full bg-violet-500" />}
        </span>
        <span className="text-slate-400">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function DateRange({
  from,
  to,
  onChange,
}: {
  from?: string;
  to?: string;
  onChange: (from?: string, to?: string) => void;
}) {
  const shortcuts: Array<[string, number | 'today']> = [
    ['Hoje', 'today'],
    ['Últimos 7 dias', 7],
    ['Últimos 15 dias', 15],
    ['Últimos 3 meses', 90],
    ['Último ano', 365],
  ];
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {shortcuts.map(([label, days]) => (
          <button
            key={label}
            type="button"
            className="rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:border-slate-400"
            onClick={() => {
              const range = periodShortcut(days);
              onChange(range.from, range.to);
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="date"
          className={`${inputClass} mt-0`}
          value={from?.slice(0, 10) ?? ''}
          onChange={(event) =>
            onChange(
              event.target.value
                ? new Date(`${event.target.value}T00:00:00`).toISOString()
                : undefined,
              to,
            )
          }
        />
        <input
          type="date"
          className={`${inputClass} mt-0`}
          value={to?.slice(0, 10) ?? ''}
          onChange={(event) =>
            onChange(
              from,
              event.target.value
                ? new Date(`${event.target.value}T23:59:59`).toISOString()
                : undefined,
            )
          }
        />
      </div>
    </div>
  );
}

function MinMax({
  min,
  max,
  onChange,
  hint,
}: {
  min?: string;
  max?: string;
  onChange: (min?: string, max?: string) => void;
  hint: string;
}) {
  return (
    <div>
      <p className="mb-2 text-xs text-slate-500">{hint}</p>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          min={0}
          placeholder="Mínimo"
          className={`${inputClass} mt-0`}
          value={min ?? ''}
          onChange={(event) => onChange(event.target.value || undefined, max)}
        />
        <input
          type="number"
          min={0}
          placeholder="Máximo"
          className={`${inputClass} mt-0`}
          value={max ?? ''}
          onChange={(event) => onChange(min, event.target.value || undefined)}
        />
      </div>
    </div>
  );
}

/**
 * Painel lateral com os 14 filtros (doc 02, 1.3). Edita um rascunho; nada vale
 * até "Aplicar filtros" — aí o rascunho vira a querystring da URL.
 */
export function LeadFilterPanel({
  initial,
  onApply,
  onClose,
}: {
  initial: LeadFilterState;
  onApply: (state: LeadFilterState) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<LeadFilterState>(initial);
  const [sourceTerm, setSourceTerm] = useState('');
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [fieldRow, setFieldRow] = useState<FieldFilterValue>({
    fieldId: '',
    operator: 'contains',
    value: '',
  });
  const [companyTerm, setCompanyTerm] = useState('');

  const set = <K extends keyof LeadFilterState>(key: K, value: LeadFilterState[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const tags = useQuery({ queryKey: ['tags'], queryFn: () => tagsApi.list() });
  const lists = useQuery({ queryKey: ['lists'], queryFn: () => listsApi.list() });
  const products = useQuery({
    queryKey: ['products'],
    queryFn: () => productsApi.list({ take: 200 }),
  });
  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants });
  const titles = useQuery({ queryKey: ['job-titles'], queryFn: listJobTitles });
  const fields = useQuery({
    queryKey: ['custom-fields', 'filter'],
    queryFn: () => customFields.list(),
  });
  const pipelines = useQuery({ queryKey: ['pipelines'], queryFn: listPipelines });
  const stages = useQuery({
    queryKey: ['stages', pipelineId],
    queryFn: () => listStages(pipelineId!),
    enabled: Boolean(pipelineId),
  });
  const companies = useQuery({
    queryKey: ['lead-picker', 'COMPANY', companyTerm],
    queryFn: () => listLeads({ take: 50, search: companyTerm || undefined, kind: 'COMPANY' }),
  });

  const leadFields = useMemo(
    () => (fields.data?.data ?? []).filter((field) => field.entity !== 'business'),
    [fields.data],
  );

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-slate-900/30"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <aside className="flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Filtros</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-lg text-slate-400"
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          <Section title="Tags" active={Boolean(draft.tags)}>
            <IdFilterControl
              value={draft.tags}
              emptyLabel="Sem tags"
              options={(tags.data?.data ?? []).map((tag) => ({
                id: tag.id,
                label: tag.name,
                color: tag.color,
              }))}
              onChange={(value) => set('tags', value)}
            />
          </Section>

          <Section
            title="Endereço"
            active={Boolean(
              draft.addressCountry ||
              draft.addressState ||
              draft.addressCity ||
              draft.addressBlock,
            )}
          >
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  ['addressCountry', 'País (BR)'],
                  ['addressState', 'UF'],
                  ['addressCity', 'Cidade'],
                  ['addressBlock', 'Bairro'],
                ] as const
              ).map(([key, label]) => (
                <input
                  key={key}
                  className={`${inputClass} mt-0`}
                  placeholder={label}
                  value={draft[key] ?? ''}
                  onChange={(event) => set(key, event.target.value || undefined)}
                />
              ))}
            </div>
          </Section>

          <Section title="Negócios" active={Boolean(draft.pipelines || draft.stages)}>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <p className="text-xs font-medium text-slate-500">Pipeline</p>
                {(pipelines.data?.data ?? []).map((pipeline) => (
                  <div key={pipeline.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.pipelines?.ids.includes(pipeline.id) ?? false}
                      onChange={() => {
                        const ids = draft.pipelines?.ids ?? [];
                        const next = ids.includes(pipeline.id)
                          ? ids.filter((id) => id !== pipeline.id)
                          : [...ids, pipeline.id];
                        set(
                          'pipelines',
                          next.length
                            ? { operator: draft.pipelines?.operator ?? 'some', ids: next }
                            : undefined,
                        );
                      }}
                    />
                    <button
                      type="button"
                      className={`text-left ${pipelineId === pipeline.id ? 'font-semibold' : ''}`}
                      onClick={() => setPipelineId(pipeline.id)}
                    >
                      {pipeline.name} ›
                    </button>
                  </div>
                ))}
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-slate-500">Etapa</p>
                {!pipelineId && (
                  <p className="text-xs text-slate-400">Escolha a pipeline (›).</p>
                )}
                {(stages.data ?? []).map((stage) => (
                  <label key={stage.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.stages?.ids.includes(stage.id) ?? false}
                      onChange={() => {
                        const ids = draft.stages?.ids ?? [];
                        const next = ids.includes(stage.id)
                          ? ids.filter((id) => id !== stage.id)
                          : [...ids, stage.id];
                        set(
                          'stages',
                          next.length
                            ? { operator: draft.stages?.operator ?? 'some', ids: next }
                            : undefined,
                        );
                      }}
                    />
                    {stage.name}
                  </label>
                ))}
              </div>
            </div>
            <select
              className={inputClass}
              value={draft.stages?.operator ?? draft.pipelines?.operator ?? 'some'}
              onChange={(event) => {
                const operator = event.target.value as IdOperator;
                if (draft.pipelines) set('pipelines', { ...draft.pipelines, operator });
                if (draft.stages) set('stages', { ...draft.stages, operator });
              }}
            >
              {(['some', 'every', 'none'] as IdOperator[]).map((operator) => (
                <option key={operator} value={operator}>
                  {OPERATOR_LABELS[operator]}
                </option>
              ))}
            </select>
          </Section>

          <Section title="Listas" active={Boolean(draft.lists)}>
            <IdFilterControl
              value={draft.lists}
              emptyLabel="Sem listas"
              options={(lists.data?.data ?? []).map((list) => ({
                id: list.id,
                label: list.name,
              }))}
              onChange={(value) => set('lists', value)}
            />
          </Section>

          <Section title="Produtos" active={Boolean(draft.products)}>
            <IdFilterControl
              value={draft.products}
              options={(products.data?.data ?? []).map((product) => ({
                id: product.id,
                label: product.name,
              }))}
              onChange={(value) => set('products', value)}
            />
          </Section>

          <Section title="Atendente" active={Boolean(draft.attendants)}>
            <IdFilterControl
              value={draft.attendants}
              operators={['some', 'none']}
              emptyLabel="Sem atendente"
              options={(attendants.data?.data ?? []).map((attendant) => ({
                id: attendant.userId,
                label: attendant.name,
              }))}
              onChange={(value) => set('attendants', value)}
            />
          </Section>

          <Section title="Origem" active={Boolean(draft.source?.length)}>
            <div className="flex gap-2">
              <input
                className={`${inputClass} mt-0`}
                placeholder="Contém…"
                value={sourceTerm}
                onChange={(event) => setSourceTerm(event.target.value)}
              />
              <Button
                onClick={() => {
                  if (!sourceTerm.trim()) return;
                  set('source', [...(draft.source ?? []), sourceTerm.trim()]);
                  setSourceTerm('');
                }}
              >
                Adicionar
              </Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {(draft.source ?? []).map((term) => (
                <button
                  key={term}
                  type="button"
                  className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                  onClick={() =>
                    set(
                      'source',
                      (draft.source ?? []).filter((item) => item !== term),
                    )
                  }
                >
                  {term} ×
                </button>
              ))}
            </div>
          </Section>

          <Section title="Cargo" active={Boolean(draft.jobTitles?.length)}>
            <div className="max-h-44 space-y-1 overflow-y-auto">
              {(titles.data ?? []).map((title) => (
                <label key={title} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.jobTitles?.includes(title) ?? false}
                    onChange={() => {
                      const current = draft.jobTitles ?? [];
                      const next = current.includes(title)
                        ? current.filter((item) => item !== title)
                        : [...current, title];
                      set('jobTitles', next.length ? next : undefined);
                    }}
                  />
                  {title}
                </label>
              ))}
              {(titles.data ?? []).length === 0 && (
                <p className="text-xs text-slate-400">Nenhum cargo cadastrado.</p>
              )}
            </div>
          </Section>

          <Section title="Empresa" active={Boolean(draft.companies)}>
            <input
              className={`${inputClass} mt-0`}
              placeholder="Buscar empresa..."
              value={companyTerm}
              onChange={(event) => setCompanyTerm(event.target.value)}
            />
            <div className="mt-2">
              <IdFilterControl
                value={draft.companies}
                operators={['some', 'none']}
                options={(companies.data?.data ?? []).map((company) => ({
                  id: company.id,
                  label: company.name,
                }))}
                onChange={(value) => set('companies', value)}
              />
            </div>
          </Section>

          <Section title="Campos adicionais" active={Boolean(draft.additionalFields?.length)}>
            <div className="space-y-2">
              <select
                className={`${inputClass} mt-0`}
                value={fieldRow.fieldId}
                onChange={(event) => setFieldRow({ ...fieldRow, fieldId: event.target.value })}
              >
                <option value="">Campo…</option>
                {leadFields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.name}
                  </option>
                ))}
              </select>
              <div className="flex gap-2">
                <select
                  className={`${inputClass} mt-0 w-32`}
                  value={fieldRow.operator}
                  onChange={(event) =>
                    setFieldRow({
                      ...fieldRow,
                      operator: event.target.value as FieldFilterValue['operator'],
                    })
                  }
                >
                  {Object.entries(FIELD_OPERATOR_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
                <input
                  className={`${inputClass} mt-0`}
                  placeholder="Valor"
                  value={fieldRow.value}
                  onChange={(event) => setFieldRow({ ...fieldRow, value: event.target.value })}
                />
              </div>
              <Button
                disabled={!fieldRow.fieldId || !fieldRow.value.trim()}
                onClick={() => {
                  set('additionalFields', [
                    ...(draft.additionalFields ?? []),
                    { ...fieldRow, value: fieldRow.value.trim() },
                  ]);
                  setFieldRow({ fieldId: '', operator: 'contains', value: '' });
                }}
              >
                Adicionar
              </Button>
              {(draft.additionalFields ?? []).map((item, index) => (
                <div
                  key={`${item.fieldId}-${index}`}
                  className="flex items-center justify-between rounded-lg bg-slate-50 px-2 py-1 text-xs"
                >
                  <span>
                    {leadFields.find((field) => field.id === item.fieldId)?.name ?? 'Campo'} ·{' '}
                    {FIELD_OPERATOR_LABELS[item.operator]} · {item.value}
                  </span>
                  <button
                    type="button"
                    className="text-slate-400"
                    onClick={() =>
                      set(
                        'additionalFields',
                        (draft.additionalFields ?? []).filter(
                          (_, position) => position !== index,
                        ),
                      )
                    }
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </Section>

          <Section
            title="Data de criação"
            active={Boolean(draft.createdAtFrom || draft.createdAtTo)}
          >
            <DateRange
              from={draft.createdAtFrom}
              to={draft.createdAtTo}
              onChange={(from, to) =>
                setDraft((current) => ({ ...current, createdAtFrom: from, createdAtTo: to }))
              }
            />
          </Section>

          <Section
            title="Última compra"
            active={Boolean(draft.lastPurchaseMin || draft.lastPurchaseMax)}
          >
            <MinMax
              hint="Última compra realizada dentro do período (dias atrás)."
              min={draft.lastPurchaseMin}
              max={draft.lastPurchaseMax}
              onChange={(min, max) =>
                setDraft((current) => ({
                  ...current,
                  lastPurchaseMin: min,
                  lastPurchaseMax: max,
                }))
              }
            />
          </Section>

          <Section
            title="Quantidade de negócios"
            active={Boolean(draft.businessesCountMin || draft.businessesCountMax)}
          >
            <MinMax
              hint="Leads com a quantidade de negócios associados no intervalo."
              min={draft.businessesCountMin}
              max={draft.businessesCountMax}
              onChange={(min, max) =>
                setDraft((current) => ({
                  ...current,
                  businessesCountMin: min,
                  businessesCountMax: max,
                }))
              }
            />
          </Section>

          <Section title="Tipo de lead" active={Boolean(draft.kind)}>
            <div className="flex gap-4 text-sm">
              {(
                [
                  [undefined, 'Todos'],
                  ['PERSON', 'Pessoa'],
                  ['COMPANY', 'Empresa'],
                ] as const
              ).map(([value, label]) => (
                <label key={label} className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    checked={draft.kind === value}
                    onChange={() => set('kind', value)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </Section>
        </div>

        <footer className="flex justify-between gap-2 border-t border-slate-200 px-4 py-3">
          <Button variant="ghost" onClick={() => setDraft({ search: draft.search })}>
            Limpar filtros
          </Button>
          <Button variant="primary" onClick={() => onApply(draft)}>
            Aplicar filtros
          </Button>
        </footer>
      </aside>
    </div>
  );
}
